import { ContextDisplayNames } from "@/AgentThread/AgentThreadSidebar/hooks/useClientViewingContext";
import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";
import { pluralize, truncateMiddle } from "@/shared/text";

import { type ContextType } from "../contextType";
import {
  getVisualizerPanelCount,
  isEmptyContext,
  describeTableState,
} from "../contextUtils";

import { ContextGroup, ContextItem } from "./ContextGroup";
import styles from "./ContextWidget.module.css";

interface ContextWidgetProps {
  context: ClientViewingContext;
  displayNames: ContextDisplayNames;
  onDelete: (contextType: ContextType, contextContent: string) => void;
}

/**
 * Widget that displays the current ClientViewingContext grouped by type.
 */
export const ContextWidget = (props: ContextWidgetProps) => {
  if (isEmptyContext(props.context)) {
    return null;
  }

  // Transform LLM context to displayable context groups
  const contextGroups = toContextGroups(props.context, props.displayNames);

  return (
    <div className={styles.contextContainer}>
      {/* Visualizer group */}
      <ContextGroup
        contextType="viz_state"
        items={contextGroups.visualizer}
        onDelete={() => props.onDelete("viz_state", "")}
      />

      {/* Dataset group */}
      <ContextGroup
        contextType="dataset"
        items={contextGroups.datasets}
        onDelete={(id) => props.onDelete("dataset", id)}
      />

      {/* Files group */}
      <ContextGroup
        contextType="file"
        items={contextGroups.files}
        onDelete={(id) => props.onDelete("file", id)}
      />

      {/* Collections group */}
      <ContextGroup
        contextType="collection"
        items={contextGroups.collections}
        onDelete={(id) => props.onDelete("collection", id)}
      />

      {/* Devices group */}
      <ContextGroup
        contextType="device"
        items={contextGroups.devices}
        onDelete={(id) => props.onDelete("device", id)}
      />

      {/* Table group: the View loaded on the table, or the table itself */}
      <ContextGroup
        contextType="table"
        items={contextGroups.table}
        onDelete={() => props.onDelete("table", "")}
      />
    </div>
  );
};

interface ContextGroups {
  datasets: ContextItem[];
  files: ContextItem[];
  collections: ContextItem[];
  devices: ContextItem[];
  table: ContextItem[];
  visualizer: ContextItem[];
}

const MAX_FILE_PATH_LENGTH = 20;
const MAX_DEVICE_ID_LENGTH = 20;

/**
 * Transforms ClientViewingContext into context items grouped by type.
 */
function toContextGroups(
  context: ClientViewingContext,
  displayNames: ContextDisplayNames,
): ContextGroups {
  // Prepare dataset items
  const datasets = context.dataset_ids.map((datasetId) => ({
    id: datasetId,
    label: datasetId,
    tooltip: displayNames.datasets[datasetId],
  }));

  // Prepare file items
  const files = context.file_ids.map((fileId) => {
    const filePath = displayNames.files[fileId] ?? fileId;
    const needsTruncation = filePath.length > MAX_FILE_PATH_LENGTH;
    return {
      id: fileId,
      label: truncateMiddle(filePath, MAX_FILE_PATH_LENGTH),
      tooltip: needsTruncation ? filePath : undefined,
    };
  });

  // Prepare collection items
  const collections = context.collection_ids.map((collectionId) => ({
    id: collectionId,
    label: collectionId,
    tooltip: displayNames.collections[collectionId],
  }));

  // Prepare device items. A device id is user-chosen and is the only name the
  // device has, so it labels the chip directly with no name to resolve — but
  // that also means it has no length bound, so truncate as file paths do.
  const devices = context.device_ids.map((deviceId) => {
    const needsTruncation = deviceId.length > MAX_DEVICE_ID_LENGTH;
    return {
      id: deviceId,
      label: truncateMiddle(deviceId, MAX_DEVICE_ID_LENGTH),
      tooltip: needsTruncation ? deviceId : undefined,
    };
  });

  // Prepare the table item. The View loaded on the table and the table's own
  // state travel together, so one chip stands for both: labeled by the View's
  // name when one is loaded, and by the table otherwise.
  const table: ContextItem[] = [];
  if (context.table_state) {
    const viewId = context.view_ids[0];
    table.push(
      viewId === undefined
        ? {
            id: "table",
            label: describeTableState(context.table_state),
            tooltip: undefined,
          }
        : {
            id: "table",
            label: displayNames.views[viewId] ?? viewId,
            tooltip: viewId,
            icon: "view",
          },
    );
  }

  // Prepare visualizer items
  const visualizer: ContextItem[] = [];
  if (context.visualizer_state) {
    const numPanels = getVisualizerPanelCount(context.visualizer_state);

    visualizer.push({
      id: "viz_state",
      label:
        numPanels === 0
          ? "empty_visualizer"
          : pluralize(`${numPanels}_viz_panel`, numPanels),
      tooltip: undefined,
    });
  }

  return { datasets, files, collections, devices, table, visualizer };
}
